import React, { useEffect } from 'react';
import { Layout, Typography, Button, Space, Table, Input, Tooltip, Pagination, Popconfirm, Tag, Row, Col, Slider, Select, Switch, Collapse, message } from 'antd';
import { PlusOutlined, SearchOutlined, DeleteOutlined, UserOutlined, FilterOutlined, StarFilled, StopOutlined, CloseCircleOutlined, ExportOutlined, ImportOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import moment from 'moment';
import PageTourButton from '../guide/PageTourButton';
import { getContactsSteps } from '../guide/toursteps';
import useContactsStore from '../../stores/useContactsStore';
import CreateContactModal from './CreateContactModal';
import ImportContactsModal from './ImportContactsModal';
import PhoneDisplay from '../common/PhoneDisplay'; // Adjusted path to common
import apiCore from '../../utils/api-core';
import session from '../../utils/session';
import config from '../../config';

const { Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;
const { Panel } = Collapse;

const ContactsV2 = () => {
    const {
        contacts,
        loading,
        pagination,
        fetchContacts,
        setPage,
        setFilters,
        setSort,
        setIsContactModalOpen,
        setIsExportModalOpen,
        exportContacts,
        deleteContact,
        // filters state
        filters,
        // filters data
        companyTags,
        availableGroups,
        availableDepartments,
        fetchFiltersData,
        setActiveCompanyId
    } = useContactsStore();

    // Derive the effective companyId: gh-admin uses the company selector (falling
    // back to session companyId when nothing is selected yet), others always use session.
    const reduxSelectedCompany = useSelector((state) => state.company?.selectedCompany);
    const userRole = session.get('userData')?.role || '';
    const isAdmin = userRole === config.userRoles.ghAdmin;
    const sessionCompanyId = session.get('userData')?.companyId;
    const companyId = (isAdmin && reduxSelectedCompany) ? reduxSelectedCompany : sessionCompanyId;

    const [showAdvanced, setShowAdvanced] = React.useState(false);
    const [localSearch, setLocalSearch] = React.useState(filters.search);
    const [localDistance, setLocalDistance] = React.useState(filters.distance !== undefined ? filters.distance : 0);
    const [importModalVisible, setImportModalVisible] = React.useState(false);

    const isSearchPending = localSearch !== filters.search;
    const areFiltersDisabled = loading || isSearchPending;

    // Sync the active company into the store and re-fetch when it changes
    useEffect(() => {
        setActiveCompanyId(companyId);
        if (companyId) {
            fetchContacts();
            fetchFiltersData();
        }
    }, [companyId]);

    useEffect(() => {
        setLocalSearch(filters.search);
    }, [filters.search]);

    useEffect(() => {
        setLocalDistance(filters.distance !== undefined ? filters.distance : 0);
    }, [filters.distance]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (localSearch !== filters.search) {
                setFilters({ search: localSearch });
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [localSearch]);

    const onImportFinish = () => {
        fetchContacts();
        fetchFiltersData();
    };

    const handleSearch = (e) => {
        setLocalSearch(e.target.value);
    };

    const handleFilterChange = (key, value) => {
        setFilters({ [key]: value });
    };

    const handleClearFilters = () => {
        setFilters({
            search: '',
            groups: [],
            tags: [],
            departments: [],
            distance: 0
        });
    };

    const handleTableChange = (pag, filters, sorter) => {
        const { field, order } = sorter;
        if (field) {
            const sortOrder = order === 'descend' ? 'desc' : 'asc';
            setSort(field, sortOrder);
        } else {
            setPage(pag.current, pag.pageSize);
        }
    };

    const confirmDelete = async (contactId) => {
        await deleteContact(contactId);
    };

    const columns = [
        ...(filters.departments && filters.departments.length > 0 ? [{
            title: 'Department',
            dataIndex: 'department',
            key: 'department',
            sorter: true,
            render: (department) => (
                <Tag color={department ? 'default' : 'error'}>
                    {department ? department.name : 'Unassigned'}
                </Tag>
            )
        }] : []),
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: 260,
            ellipsis: true,
            render: (text, record) => (
                <Space style={{ maxWidth: '100%' }}>
                    <UserOutlined />
                    <Link
                        to={`/contacts/${record.id}`}
                        style={{
                            color: 'inherit',
                            textDecoration: 'none',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'inline-block',
                            maxWidth: 140,
                            verticalAlign: 'middle'
                        }}
                        title={text}
                    >
                        {text}
                    </Link>
                    {record.star && <StarFilled style={{ color: 'gold', fontSize: '12px' }} />}
                    {record.blacklist && (
                        <Tooltip title="Opted out">
                            <StopOutlined style={{ color: '#ff4d4f', fontSize: '12px' }} />
                        </Tooltip>
                    )}
                    {record.deleted_at && (
                        <Tag color='orange' style={{ fontSize: '10px', lineHeight: '16px', marginLeft: 4, flexShrink: 0, whiteSpace: 'nowrap' }}>Archived</Tag>
                    )}
                </Space>
            ),
            sorter: true
        },
        {
            title: 'Mobile',
            dataIndex: 'mobile',
            key: 'mobile',
            sorter: true,
            render: (mobile) => <PhoneDisplay value={mobile} />
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            sorter: true,
            render: (text) => text || '-'
        },
        {
            title: 'Created At',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (text) => text ? moment(text).format('MMM DD, YYYY') : '-',
            sorter: true
        },
        {
            title: 'Groups',
            dataIndex: 'groups', 
            key: 'groups',
            sorter: true,
            render: (groups) => {
                if (!groups || groups.length === 0) return '-';
                
                let displayGroups = [];
                let remainderCount = 0;
                let remainderTooltip = '';
                const filterIds = filters.groups || [];

                if (filterIds.length > 0) {
                     const matched = groups.filter(g => filterIds.includes(g.id));
                     const others = groups.filter(g => !filterIds.includes(g.id));
                     
                     if (matched.length > 0) {
                         displayGroups = matched;
                         remainderCount = others.length;
                         remainderTooltip = others.map(g => g.name).join(', ');
                     } else {
                         displayGroups = [groups[0]];
                         remainderCount = groups.length - 1;
                         remainderTooltip = groups.slice(1).map(g => g.name).join(', ');
                     }
                } else {
                    displayGroups = [groups[0]];
                    remainderCount = groups.length - 1;
                    remainderTooltip = groups.slice(1).map(g => g.name).join(', ');
                }

                return (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
                         {displayGroups.map((g, i) => (
                            <Tag
                                key={g.id || i}
                                style={{
                                    maxWidth: 120,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    margin: 0,
                                    color: 'var(--gh-info, #1E88E5)',
                                    background: 'rgba(30,136,229,0.10)',
                                    borderColor: 'rgba(30,136,229,0.25)',
                                    borderRadius: 999
                                }}
                            >
                                {g.name}
                            </Tag>
                         ))}
                        {remainderCount > 0 && (
                            <Tooltip title={remainderTooltip}>
                                <Tag style={{ margin: 0 }}>+{remainderCount}</Tag>
                            </Tooltip>
                        )}
                    </div>
                );
            }
        },
        {
            title: 'Tags',
            dataIndex: 'tags', 
            key: 'tags',
            sorter: true,
            render: (tags) => {
                if (!tags || tags.length === 0) return '-';

                let displayTags = [];
                let remainderCount = 0;
                let remainderTooltip = '';
                const filterTags = filters.tags || [];

                if (filterTags.length > 0) {
                     const matched = tags.filter(t => filterTags.includes(t));
                     const others = tags.filter(t => !filterTags.includes(t));

                     if (matched.length > 0) {
                         displayTags = matched;
                         remainderCount = others.length;
                         remainderTooltip = others.join(', ');
                     } else {
                         displayTags = [tags[0]];
                         remainderCount = tags.length - 1;
                         remainderTooltip = tags.slice(1).join(', ');
                     }
                } else {
                    displayTags = [tags[0]];
                    remainderCount = tags.length - 1;
                    remainderTooltip = tags.slice(1).join(', ');
                }

                return (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
                         {displayTags.map((tag, i) => (
                            <Tag
                                key={tag || i}
                                style={{
                                    maxWidth: 120,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    margin: 0,
                                    color: 'var(--gh-primary-active, #2E7D32)',
                                    background: 'var(--gh-primary-tint, #E8F5E9)',
                                    borderColor: 'var(--gh-primary-border, #A5D6A7)',
                                    borderRadius: 999
                                }}
                            >
                                {tag}
                            </Tag>
                         ))}
                        {remainderCount > 0 && (
                            <Tooltip title={remainderTooltip}>
                                <Tag style={{ margin: 0 }}>+{remainderCount}</Tag>
                            </Tooltip>
                        )}
                    </div>
                );
            }
        },
        {
            title: 'Actions',
            key: 'action',
            render: (_, record) => (
                <Popconfirm
                    title="Delete contact"
                    description="Are you sure to delete this contact?"
                    onConfirm={() => confirmDelete(record.id)}
                    okText="Yes"
                    cancelText="No"
                >
                    <Button type="text" danger icon={<DeleteOutlined />} />
                </Popconfirm>
            ),
        },
    ];

    return (
        <Layout className="gh-compact-page">
             <Content className="gh-compact-panel" style={{ overflowY: 'auto' }}>
                                {/* Header Section */}
                                <div
                                    className="tour-contacts-header"
                                    style={{
                                        marginBottom: 16,
                                        background: 'linear-gradient(110deg, #F6FFED 0%, #E6F7FF 100%)',
                                        border: '1px solid #d9f7be',
                                        padding: 20
                                    }}
                                >
                                    <Row gutter={[16, 16]} align="middle" justify="space-between">
                                        <Col xs={24} md={16}>
                                            <Space direction="vertical" size={6}>
                                                <Title level={3} style={{ margin: 0, color: '#1f2937' }}>Contacts <PageTourButton steps={getContactsSteps()} /></Title>
                                                <Text style={{ fontSize: 15, color: '#4b5563' }}>
                                                    Manage your audience, import new leads, and organize your recruitment database for targeted outreach.
                                                </Text>
                                                <Space size={16}>
                                                    <Space size={6}>
                                                        <UserOutlined style={{ color: '#08979c' }} />
                                                        <Text type="secondary">{pagination.totalActiveContacts || 0} total contacts</Text>
                                                    </Space>
                                                    <Space size={6}>
                                                        <FilterOutlined style={{ color: '#08979c' }} />
                                                        <Text type="secondary">Organized by groups & tags</Text>
                                                    </Space>
                                                </Space>
                                            </Space>
                                        </Col>
                                        <Col xs={24} md={8} style={{ textAlign: 'right' }}>
                                            <Space wrap className="tour-contacts-actions">
                                                <Button
                                                    icon={<ExportOutlined />}
                                                    onClick={exportContacts}
                                                >
                                                    Export
                                                </Button>
                                                <Button
                                                    icon={<ImportOutlined />}
                                                    onClick={() => setImportModalVisible(true)}
                                                >
                                                    Import
                                                </Button>
                                                <Button
                                                    type="primary"
                                                    icon={<PlusOutlined />}
                                                    onClick={() => setIsContactModalOpen(true)}
                                                >
                                                    Add Contact
                                                </Button>
                                            </Space>
                                        </Col>
                                    </Row>
                                </div>

                <div style={{padding:'0 20px 20px'}}>
                    {/* Filters Section */}
                    <div className="gh-compact-toolbar" style={{ cursor: areFiltersDisabled ? 'wait' : 'default' }}>

                        <Row gutter={[16, 16]} align="middle">
                            <Col span={6}>
                                <Input 
                                    placeholder="Search by Name, Mobile, Email" 
                                    prefix={<SearchOutlined />} 
                                    onChange={handleSearch}
                                    disabled={loading}
                                    allowClear
                                    value={localSearch}
                                />
                            </Col>
                            
                            <Col span={6}>
                                <Select
                                    mode="multiple"
                                    placeholder="Filter by Groups"
                                    disabled={areFiltersDisabled}
                                    style={{ width: '100%' }}
                                    onChange={(val) => handleFilterChange('groups', val)}
                                    value={filters.groups}
                                    maxTagCount="responsive"
                                    allowClear
                                >
                                    {availableGroups.map(g => <Option key={g.id} value={g.id}>{g.name}</Option>)}
                                </Select>
                            </Col>

                            <Col span={6}>
                                <Select
                                    mode="multiple"
                                    placeholder="Filter by Tags"
                                    disabled={areFiltersDisabled}
                                    style={{ width: '100%' }}
                                    onChange={(val) => handleFilterChange('tags', val)}
                                    value={filters.tags}
                                    maxTagCount="responsive"
                                    allowClear
                                >
                                    {companyTags.map(tag => <Option key={tag} value={tag}>{tag}</Option>)}
                                </Select>
                            </Col>

                            <Col span={6} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
                                <Button 
                                    type={showAdvanced ? 'primary' : 'default'}
                                    disabled={areFiltersDisabled}
                                    icon={<FilterOutlined />} 
                                    onClick={() => setShowAdvanced(!showAdvanced)}
                                >
                                    Advanced Filters
                                </Button>
                            </Col>
                        </Row>
                        
                        {showAdvanced && (
                            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
                                <Row gutter={[16, 16]}>
                                    <Col span={6}>
                                        <Space direction="vertical" style={{ width: '100%' }}>
                                            <Text type="secondary" style={{ fontSize: '12px' }}>Department</Text>
                                            <Select 
                                                mode="multiple"
                                                disabled={areFiltersDisabled}
                                                placeholder="Select Departments"
                                                style={{ width: '100%' }}
                                                onChange={(val) => handleFilterChange('departments', val)}
                                                value={filters.departments}
                                                maxTagCount="responsive"
                                            >
                                                <Option value="unassigned">Unassigned</Option>
                                                {availableDepartments.map(dept => (
                                                    <Option key={dept.id} value={dept.id}>{dept.name}</Option>
                                                ))}
                                            </Select>
                                        </Space>
                                    </Col>
                                    <Col span={8}>
                                        <Space direction="vertical" style={{ width: '100%' }}>
                                            <Text type="secondary" style={{ fontSize: '12px' }}>Distance</Text>
                                            <Tooltip title={
                                                (!filters.departments || filters.departments.length === 0) ? "Select a department to enable distance filter" :
                                                (!filters.departments.some(id => availableDepartments.find(d => d.id === id)?.zipcode)) ? "Selected department(s) have no location data" : ""
                                            }>
                                                <div style={{ display: 'block' }}>
                                                    <Slider 
                                                        disabled={areFiltersDisabled || !filters.departments || filters.departments.length === 0 || !filters.departments.some(id => availableDepartments.find(d => d.id === id)?.zipcode)}
                                                        min={0}
                                                        max={30}
                                                        step={1}
                                                        marks={{ 
                                                            0: { style: { whiteSpace: 'nowrap' }, label: '0 mi' }, 
                                                            15: { style: { whiteSpace: 'nowrap' }, label: '15 mi' }, 
                                                            30: { style: { whiteSpace: 'nowrap' }, label: '30 mi' } 
                                                        }}
                                                        value={(!filters.departments || filters.departments.length === 0) ? 0 : localDistance}
                                                        onChange={(val) => setLocalDistance(val)}
                                                        onAfterChange={(val) => handleFilterChange('distance', val)}
                                                    />
                                                </div>
                                            </Tooltip>
                                        </Space>
                                    </Col>
                                    <Col span={10} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <Space direction="vertical" align="end">
                                            <Text type="secondary" style={{ fontSize: '12px' }}>Archive Status</Text>
                                            <div style={{ display: 'flex', alignItems: 'center', height: '32px' }}>
                                                <Space>
                                                    <Text>Show Archived</Text>
                                                    <Switch 
                                                        checked={filters.archived} 
                                                        disabled={areFiltersDisabled}
                                                        onChange={(checked) => handleFilterChange('archived', checked)} 
                                                    />
                                                </Space>
                                            </div>
                                        </Space>
                                    </Col>
                                </Row>
                            </div>
                        )}
                    </div>

                    {/* Table Section */}
                    <div className="gh-compact-table-wrap" style={{ background: '#fff' }}>
                        {(filters.search || filters.tags.length > 0 || filters.groups.length > 0 || (filters.departments && filters.departments.length > 0)) && (
                            <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Text type="secondary">Showing {pagination.total} matches for your search.</Text>
                                <Button 
                                    type="text" 
                                    size="small" 
                                    icon={<CloseCircleOutlined />} 
                                    onClick={handleClearFilters}
                                    style={{ color: '#8c8c8c' }} // Subtle gray
                                />
                            </div>
                        )}
                        <Table 
                            className="gh-themed-table"
                            columns={columns} 
                            dataSource={contacts} 
                            rowKey="id" 
                            loading={loading}
                            size="small"
                            pagination={{
                                current: pagination.page,
                                pageSize: pagination.pageSize,
                                total: pagination.total,
                                showSizeChanger: true
                            }}
                            onChange={handleTableChange}
                        />
                    </div>
                </div>
                <CreateContactModal />
                <ImportContactsModal 
                    visible={importModalVisible}
                    onCancel={() => setImportModalVisible(false)}
                    onFinish={onImportFinish}
                    mode="execute"
                    availableGroups={availableGroups}
                />
             </Content>
        </Layout>
    );
};

export default ContactsV2;
